import ResponsiveImage from 'components/Common/ResponsiveImage'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import React, { useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import TextButton from 'LuxKit/Button/TextButton'
import ContactContext from 'contexts/contactContext'
import TextLink from 'LuxKit/TextLink'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import WaitingListRequestFormModal from 'components/OfferPage/TourV2/WaitingListRequest/WaitingListRequestFormModal'

const CardActions = styled.div`
  border-top: 1px solid ${props => props.theme.palette.neutral.default.five};
  padding: ${rem(16)} 0 0 0;
  height: 100%;
  align-items: center;

  ${mediaQueryUp.tablet} {
    align-items: flex-start;
    border-top: none;
    border-left: 1px solid ${props => props.theme.palette.neutral.default.five};
    padding: 0 0 0 ${rem(24)};
  }
`

const CardBody = styled(Group)`
  padding: ${rem(16)};

  ${mediaQueryUp.tablet} {
    padding: ${rem(24)};
  }
`

const CardImageContainer = styled.div`
  > * {
    height: 100%;
  }
`

const UnavailabilityCard = styled(Group)`
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  background-color: ${props => props.theme.palette.neutral.default.eight};

  > ${CardImageContainer} {
    flex-basis: ${rem(230)};
  }

  > ${CardBody} {
    flex-grow: 1;
  }
`

interface Props {
  offer: App.Tours.TourV2Offer
  offerVariation: App.Tours.TourV2OfferVariation
}

function TourV2UnavailabilityCard(props: Props) {
  const { offer, offerVariation } = props
  const intl = useIntl()
  const currentContact = useContext(ContactContext)
  const contact = currentContact.defaultContact
  const offerImage = offerVariation.images[0]

  const showModal = useContext(ModalContext)
  const waitingListSoldTourModal = useCallback(() => {
    showModal(<WaitingListRequestFormModal offer={offer} />)
  }, [showModal, offer])

  return <UnavailabilityCard direction="vertical" tabletDirection="horizontal">
    {!!offerImage?.id && <CardImageContainer>
      <ResponsiveImage aspectRatio="5:3" id={offerImage.id} mobileWidth="230px" fit="center" />
    </CardImageContainer>}
    <CardBody direction="vertical" gap={16} tabletDirection="horizontal" tabletGap={24} tabletVerticalAlign="center">
      <div>
        <Heading variant="heading5">
          {intl.formatMessage({
            id: 'FJaNIrcARz',
            defaultMessage: 'Sorry, this offer has sold out!',
            description: 'Heading when tour offer is sold out',
          })}
        </Heading>
        <BodyText variant="medium">{offerVariation.name}</BodyText>
      </div>
      <CardActions>
        <Group direction="vertical" gap={16}>
          <div>
            <BodyText variant="medium" weight="bold">
              {intl.formatMessage({
                id: 'fdL7barp0M',
                defaultMessage: 'Still interested?',
                description: 'Subheading for waitlist signup',
              })}
            </BodyText>
            <BodyText variant="medium">
              {intl.formatMessage({
                id: 'idCGkB+az8',
                defaultMessage: 'Join the waitlist to be the first to know when it becomes available again.',
                description: 'Description text for waitlist signup',
              })}
            </BodyText>
          </div>
          <Group desktopDirection="horizontal-reverse" direction="vertical" gap={16} verticalAlign="center" horizontalAlign="center" desktopHorizontalAlign="end" fullWidth>
            <TextButton fit="mobile-full-width" kind="primary" onClick={waitingListSoldTourModal}>
              {intl.formatMessage({
                id: 'IKKu+3ROxZ',
                defaultMessage: 'Join the waitlist',
                description: 'Button text to join waitlist',
              })}
            </TextButton>
            <TextLink
              size="medium"
              weight="regular"
              href={`tel:${contact.number}`}
            >
              {intl.formatMessage(
                {
                  id: 'RwYHrwxoUM',
                  defaultMessage: 'Call {contactNumber}',
                  description: 'Link text to call contact number',
                },
                { contactNumber: contact.humanReadable },
              )}
            </TextLink>
          </Group>
        </Group>
      </CardActions>
    </CardBody>
  </UnavailabilityCard>
}

export default TourV2UnavailabilityCard
